import { getAuth, onAuthStateChanged } from "firebase/auth";
import { addDoc, collection, deleteDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import React, { useEffect, useState } from 'react';
import { VscArrowLeft } from "react-icons/vsc";
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import "../css/EditProductModal.css";
import { db } from "../firebase-config";

const EditIngredient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [measurement, setMeasurement] = useState('');
  const [dealerPrice, setDealerPrice] = useState('');
  const [sellerPrice, setSellerPrice] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');
  const [usertype, setUsertype] = useState('');

  const auth = getAuth(); // Get the Firebase Auth instance
  const currentUser = auth.currentUser; // Get the current logged-in user
  
  useEffect(() => {
    if (currentUser) {
      setLastUpdatedBy(currentUser.email); // Automatically set the logged-in user's email
    }
  }, [currentUser]);

  // Add a new state to store the original quantity
  const [originalQuantity, setOriginalQuantity] = useState('');
  const [originaldealerPrice, setOriginaldealerPrice] = useState('');

  useEffect(() => {
    const fetchIngredients = async () => {
      try {
        if (!id) {
          setError('No ingredient ID provided');
          console.error('No ingredient ID found in URL parameters');
          return;
        }

        const docRef = doc(db, 'ingredients', id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setName(docSnap.data().name);
          setQuantity(docSnap.data().quantity);
          setMeasurement(docSnap.data().measurement);
          setDealerPrice(docSnap.data().dealerPrice);
          setOriginaldealerPrice(docSnap.data().dealerPrice);
          setSellerPrice(docSnap.data().sellerPrice);
          setOriginalQuantity(docSnap.data().quantity);
        } else {
          setError('Ingredient not found');
        }
      } catch (err) {
        setError('Error fetching ingredient details');
        console.error(err);
      }
      setLoading(false);
    };

    fetchIngredients();
  }, [id]);
  
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Query the users collection to get the user type
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUsertype(userData.usertype);  // Set usertype based on the Firestore document
        }
      }
    });

    return () => unsubscribe();
  }, []);

  const addItemLog = async (itemData) => {
    try {
      const currentDate = new Date();

      const formattedDate = [
        currentDate.getMonth() + 1,
        currentDate.getDate(),
        currentDate.getFullYear(),
      ].join('/') + ' ' +
      [currentDate.getHours(), currentDate.getMinutes()].join(':');

      if (originalQuantity !== null && originalQuantity !== undefined &&
        itemData.quantity !== null && itemData.quantity !== undefined &&
        Math.abs(originalQuantity - itemData.quantity) < Number.EPSILON) {
      console.log("Items are the same, skipping log entry.");
      return; // Exit early if prices haven't changed
    }

      await addDoc(collection(db, 'item_logs'), {
        itemName: itemData.name,
        typeOfChange: 'Edited',
        lastUpdatedBy: lastUpdatedBy.split('@')[0],
        date: formattedDate,
        descriptionOfChange: '-',
        quantity: '-',
        editChangeLog: `${originalQuantity} -> ${itemData.quantity}`,
      });
    } catch (error) {
      console.error('Error adding log:', error);
    }
  };

  const addPriceLog = async (priceData) => {
    try {
      const currentDate = new Date();

      const formattedDate = [
        currentDate.getMonth() + 1,
        currentDate.getDate(),
        currentDate.getFullYear(),
      ].join('/') + ' ' +
      [currentDate.getHours(), currentDate.getMinutes()].join(':');

      if (originaldealerPrice !== null && originaldealerPrice !== undefined &&
        priceData.dealerPrice !== null && priceData.dealerPrice !== undefined &&
        Math.abs(originaldealerPrice - priceData.dealerPrice) < Number.EPSILON) {
      console.log("Prices are the same, skipping log entry.");
      return; // Exit early if prices haven't changed
    }

      await addDoc(collection(db, 'price_logs'), {
        supplierName: '-',
        itemName: priceData.name,
        typeOfChange: 'Edited',
        lastUpdatedBy: lastUpdatedBy.split('@')[0],
        date: formattedDate,
        priceChangeLog: `${originaldealerPrice} -> ${priceData.dealerPrice}`,
      });
    } catch (error) {
      console.error('Error adding log:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const confirmed = await Swal.fire({
      title: 'Are you sure?',
      text: "You sure you want to update this ingredient?",
      icon: 'ok',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
    });
    if (!confirmed.isConfirmed) {
      return; // If the user cancels, stop the update action
    }

    try {
      if (!id) {
        setError('No ingredient ID provided');
        console.error('No ingredient ID found for update operation');
        return;
      }

      // Log the edit action before updating
      await addItemLog({ name, quantity, lastUpdatedBy });
      await addPriceLog({ name, dealerPrice, lastUpdatedBy });

      const docRef = doc(db, 'ingredients', id);
      await updateDoc(docRef, {
        name,
        quantity,
        measurement,
        dealerPrice,
        sellerPrice,
        lastUpdatedBy
      });

      toast.success("Ingredient updated successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
      });
      navigate('/pages/InventoryIngredients');
    } catch (err) {
      setError('Failed to update ingredient details');
      console.error(err);
    }
  };

  const handleDelete = async () => {
    const confirmDelete = await Swal.fire({
      title: 'Are you sure?',
      text: "You want to delete this ingredient. This action cannot be undone.",
      icon: 'warning',
      input: 'text',
      inputPlaceholder: 'Type "confirm"',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    });
    
    if (!confirmDelete.isConfirmed || confirmDelete.value.toLowerCase() !== 'confirm') {
      return; // If the user cancels, stop the delete action
    }

    try {
      if (!id) {
        setError('No ingredient ID provided');
        console.error('No ingredient ID found for delete operation');
        return;
      }

      const docRef = doc(db, 'ingredients', id);
      await deleteDoc(docRef);
      toast.success("Ingredient deleted successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: true,
        progress: undefined,
      });
      navigate('/pages/InventoryIngredients'); // Redirect to the inventory page after deletion
    } catch (err) {
      setError('Failed to delete ingredient');
      console.error(err);
    }
  };

  if (error) return <div>Error: {error}</div>;

  return (
    <form onSubmit={handleSubmit} className="edit-ingredient-form">
        <div className="close-button" onClick={() => navigate('/pages/InventoryIngredients')}>
        <VscArrowLeft />
      </div>
      <h1>Edit Ingredient Details</h1>
      <label htmlFor="ingredientName">Name:</label>
      <input
        id="ingredientName"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Name"
        disabled={!(usertype === "Admin")}
        required
      />
      <label htmlFor="quantity">Quantity:</label>
      <input
        id="quantity"
        type="number"
        step="0.01"
        value={quantity}
        onChange={(e) => setQuantity(e.target.value)}
        placeholder="Quantity"
        min="0"
        required
      />
      <label htmlFor="ingredientName">Unit of Measurement:</label>
      <input
        id="unitOfMeasurement"
        type="text"
        value={measurement}
        onChange={(e) => setMeasurement(e.target.value)}
        placeholder="Unit of Measurement"
        disabled={!(usertype === "Admin")}
        required
      />
      <label htmlFor="dealerPrice">Dealer's Price:</label>
      <input
        id="dealerPrice"
        type="number"
        step="0.01"
        value={dealerPrice}
        onChange={(e) => setDealerPrice(e.target.value)}
        placeholder="Dealer's Price"
        min="0"
        required
      />
      <label htmlFor="sellerPrice">Seller's Price:</label>
      <input
        id="sellerPrice"
        type="number"
        step="0.01"
        value={sellerPrice}
        onChange={(e) => setSellerPrice(e.target.value)}
        placeholder="Seller's Price"
        min="0"
        required
      />
      <div className="form-buttons">
        <button type="submit" className="btn-updateingredient" >Update Ingredient</button>
        <button 
          type="button"
          onClick={handleDelete}
          className="delete-btn"
          disabled={!(usertype === "Admin")}
        >
          Delete Ingredient
        </button>
      </div>
    </form>
  );
};

export default EditIngredient;